// Single source of truth for Family Camp's tiered pricing.
//
// Mirrors services/cart/pricing.js's FAMILY_CAMP_CONFIG — that copy is what actually prices the
// cart and protects the charge; this one is what the wizard shows while the registrant is still
// deciding, so the number on screen never disagrees with what gets charged. Keep the two in sync
// if the ladder ever changes.
//
// Only the standard tier's first-camper price lives in the event table (registration_fee + camp_fee)
// — admin-adjustable without a code change. Everything else here is a fixed dollar schedule that
// does not vary per event, so it has no column of its own.

export const DELUXE_BASE_SURCHARGE = 140
export const DELUXE_MAX_CAMPERS = 5

// Position 1 (the registrant) is priced from the event row, not listed here. Each of these prices
// already includes the $30 registration fee, exactly as event.registration_fee + event.camp_fee
// does for position 1.
export const FAMILY_CAMP_LADDERS = {
   standard: { 2: 295, 3: 275, 4: 210, 5: 195, overflow: 185 },
   deluxe: { 2: 310, 3: 190, 4: 185, 5: 165 },
}

export const RoomTier = {
   Standard: 'standard',
   Deluxe: 'deluxe',
}

// What one additional family member at this position costs, in whole dollars, including their $30
// registration fee. Deluxe has no position beyond its 5-camper cap — getFamilyMembers below is what
// actually enforces that cap in the UI, so this fallback is only ever a defensive last resort.
export function getLadderPrice(tier, position) {
   const ladder = FAMILY_CAMP_LADDERS[tier] ?? FAMILY_CAMP_LADDERS.standard
   if (ladder[position] !== undefined) {
      return ladder[position]
   }
   return tier === RoomTier.Deluxe ? ladder[DELUXE_MAX_CAMPERS] : ladder.overflow
}

// Position 1's price: read from the event row for standard, plus the flat deluxe surcharge when
// that tier is chosen. This is the only figure in the whole ladder that comes from the database.
export function getBasePrice(week, tier) {
   const standardBase = Number(week?.registration_fee ?? 0) + Number(week?.camp_fee ?? 0)
   return tier === RoomTier.Deluxe ? standardBase + DELUXE_BASE_SURCHARGE : standardBase
}

// Blank rows are filtered the same way the server does before storing family_member rows, so
// the price on screen always matches the roster that gets submitted.
export function getFamilyMembers(personalInformation) {
   return (personalInformation?.familyMembers ?? [])
      .filter(m => (m?.firstName ?? '').trim().length > 0 || (m?.lastName ?? '').trim().length > 0)
}

export function getAttendeeCount(personalInformation) {
   return 1 + getFamilyMembers(personalInformation).length
}

// Every person on the registration, in order, with what they each cost. Position 1 is the
// registrant; family members start at position 2, in the order they were added.
export function getRosterLines(week, tier, personalInformation) {
   const members = getFamilyMembers(personalInformation)
   const roster = [{
      position: 1,
      firstName: personalInformation?.firstName ?? '',
      lastName: personalInformation?.lastName ?? '',
      price: getBasePrice(week, tier),
   }]
   members.forEach((member, index) => {
      const position = index + 2
      roster.push({
         position,
         firstName: member.firstName ?? '',
         lastName: member.lastName ?? '',
         price: getLadderPrice(tier, position),
      })
   })
   return roster
}

export function getRegistrationFeeDue(week, personalInformation) {
   return Number(week?.registration_fee ?? 0) * getAttendeeCount(personalInformation)
}

export function getTotal(week, tier, personalInformation) {
   return getRosterLines(week, tier, personalInformation)
      .reduce((sum, line) => sum + line.price, 0)
}

export function getCampFeeDue(week, tier, personalInformation) {
   return getTotal(week, tier, personalInformation) - getRegistrationFeeDue(week, personalInformation)
}

// Deluxe rooms cap the whole party, the registrant included, at 5 people — standard has no cap.
export function canAddFamilyMember(tier, personalInformation) {
   if (tier !== RoomTier.Deluxe) {
      return true
   }
   return getAttendeeCount(personalInformation) < DELUXE_MAX_CAMPERS
}
